import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { BookmarkSimple, Trash, User, ArrowRight, ShieldStar, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [bm, rp] = await Promise.all([
        axios.get(`${API}/bookmarks`).then((r) => r.data).catch(() => []),
        axios.get(`${API}/career-deepdive/reports`).then((r) => r.data).catch(() => []),
      ]);
      setBookmarks(bm || []);
      setReports(rp || []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user) load(); // eslint-disable-line
  }, [user, loading, navigate, load]);

  // eslint-disable-next-line
  const remove = async (slug) => {
    try {
      await axios.delete(`${API}/bookmarks/${slug}`);
      setBookmarks((bs) => bs.filter((b) => b.slug !== slug));
      toast.success("Removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-slate-600">Loading…</div>;
  }

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-slate-200" />
        ) : (
          <span className="h-14 w-14 rounded-full bg-[#0033A0] text-white flex items-center justify-center">
            <User size={26} weight="fill" />
          </span>
        )}
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900" data-testid="dashboard-greeting">
            Welcome, {user.name?.split(" ")[0] || "service member"}.
          </h1>
          <p className="text-slate-600 text-sm" data-testid="dashboard-email">{user.email}</p>
        </div>
      </div>

      <section className="mt-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldStar size={22} weight="duotone" className="text-[#0033A0]" /> My Career Deep Dives
          </h2>
          <Link to="/career-deepdive" data-testid="dashboard-buy-deepdive" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white text-sm font-semibold py-2 px-4 rounded-md">
            <Sparkle size={14} weight="fill" /> Buy a report — $1
          </Link>
        </div>
        {reports.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-sm text-slate-600" data-testid="empty-reports">
            No reports yet. Premium Career Deep Dives appear here right after purchase.
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="reports-list">
            {reports.map((r) => (
              <li key={r.report_id} className="bg-white border border-slate-200 rounded-xl p-4">
                <Link to={`/career-deepdive/reports/${r.report_id}`} className="block">
                  <p className="font-display font-bold text-slate-900">{r.branch_display} Deep Dive</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{r.jobs?.join(" • ")}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookmarkSimple size={22} weight="bold" /> Saved guides
          </h2>
        </div>

        {busy ? (
          <p className="text-slate-500">Loading bookmarks…</p>
        ) : bookmarks.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center" data-testid="empty-bookmarks">
            <p className="text-slate-700">No saved guides yet.</p>
            <Link to="/topics" className="mt-4 inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-2.5 px-5 rounded-md transition-colors">
              Browse topics <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="bookmarks-list">
            {bookmarks.map((b) => (
              <li key={b.slug} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
                <Link to={`/topics/${b.slug}`} className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{b.title}</p>
                  <p className="text-xs text-slate-500">/{b.slug}</p>
                </Link>
                <button
                  onClick={() => remove(b.slug)}
                  className="text-slate-400 hover:text-red-600"
                  aria-label="Remove bookmark"
                  data-testid={`remove-bookmark-${b.slug}`}
                >
                  <Trash size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/start-here" className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0033A0] hover:shadow-md transition-all">
          <p className="text-xs font-bold uppercase tracking-wider text-[#0033A0]">Beginner</p>
          <p className="font-display font-bold text-slate-900 mt-1">Start Here guide</p>
        </Link>
        <Link to="/compare" className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0033A0] hover:shadow-md transition-all">
          <p className="text-xs font-bold uppercase tracking-wider text-[#0033A0]">Decision</p>
          <p className="font-display font-bold text-slate-900 mt-1">Compare branches</p>
        </Link>
        <Link to="/faq" className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0033A0] hover:shadow-md transition-all">
          <p className="text-xs font-bold uppercase tracking-wider text-[#0033A0]">Quick</p>
          <p className="font-display font-bold text-slate-900 mt-1">Common questions</p>
        </Link>
      </section>
    </main>
  );
}
